import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {forkJoin, Observable, Observer} from 'rxjs';

import {Constants} from '../../shared/config/constants.config';
import {DiscountViewModel, PricingViewModel} from '../viewModels/pricing.viewModel';
import {PaymentMethodViewModel} from '../../shared/viewModels/paymentMethod.viewModel';
import {
    CreateSubscriptionAddonTransactionRequestDTO,
    CreateSubscriptionRequestDTO,
    YettelSubscriptionExistsDTO
} from '../dto/createSubscriptionRequest.dto';
import {CreateSubscriptionResponseDTO} from '../dto/createSubscriptionResponse.dto';
import {BraintreeClientTokenViewModel} from '../../shared/viewModels/braintreeClientToken.viewModel';
import {DiscountRequestDTO} from '../dto/discountRequest.dto';
import {SubscriptionViewModel} from '../../shared/viewModels/subscription.viewModel';
import {UsersService} from '../../shared/services/users.service';
import {UserProfileViewModel} from '../../shared/viewModels/userProfile.viewModel';


@Injectable()
export class PricingService {
    private userSubscription: SubscriptionViewModel = null;
    private isMasterUser: boolean = null;

    public permissions = {
        'AUTO_FILL_DIARY_RECIEPS': 'AUTO_FILL_DIARY_RECIEPS',
        'EXTERNAL_MEALS': 'EXTERNAL_MEALS',
        'SHOPPING_LIST': 'SHOPPING_LIST',
        'DAY_TRAINING': 'DAY_TRAINING',
        'EXTERNAL_LOAD': 'EXTERNAL_LOAD',
        'COPY_DIARY_RECIPES': 'COPY_DIARY_RECIPES',
        'COPY_DIARY_SINGLE_RECIPE': 'COPY_DIARY_SINGLE_RECIPE',
        'DELETE_DIARY_RECIPES': 'DELETE_DIARY_RECIPES',
    };

    private freePlanPermissions = [];
    private lightPlanPermissions = [
        this.permissions['EXTERNAL_MEALS'],
        this.permissions['SHOPPING_LIST']
    ];
    private standartPlanPermissions = [
        this.permissions['AUTO_FILL_DIARY_RECIEPS'],
        this.permissions['EXTERNAL_MEALS'],
        this.permissions['SHOPPING_LIST'],
        this.permissions['DAY_TRAINING'],
        this.permissions['EXTERNAL_LOAD'],
        this.permissions['COPY_DIARY_RECIPES'],
        this.permissions['COPY_DIARY_SINGLE_RECIPE'],
        this.permissions['DELETE_DIARY_RECIPES']
    ];

    constructor(
        private readonly constants: Constants,
        private readonly usersService: UsersService,
        private readonly httpClient: HttpClient
    ) {
    }

    getPricings(): Observable<PricingViewModel[]> {
        return this.httpClient.get<PricingViewModel[]>(`${this.constants.apiURL()}/pricing/`);
    }

    getPaymentMethods(): Observable<PaymentMethodViewModel[]> {
        return this.httpClient.get<PaymentMethodViewModel[]>(`${this.constants.apiURL()}/payment/methods/`);
    }

    createSubscription(createSubscriptionDTO: CreateSubscriptionRequestDTO): Observable<CreateSubscriptionResponseDTO> {
        return this.httpClient.post<CreateSubscriptionResponseDTO>(`${this.constants.apiURL()}/subscription/`, createSubscriptionDTO);
    }

    createYettelSubscription(): Observable<void> {
        return this.httpClient.post<void>(`${this.constants.apiURL()}/subscription/yettel/`, {});
    }

    getYettelSubscription(): Observable<YettelSubscriptionExistsDTO> {
        return this.httpClient.get<YettelSubscriptionExistsDTO>(`${this.constants.apiURL()}/subscription/yettel/`);
    }

    braintreeGenerateClientToken(): Observable<BraintreeClientTokenViewModel> {
        return this.httpClient.get<BraintreeClientTokenViewModel>(`${this.constants.apiURL()}/braintree/client-token/`);
    }

    createSubscriptionAddonTransaction(createSubscriptionRequestDTO: CreateSubscriptionAddonTransactionRequestDTO): Observable<void> {
        return this.httpClient.post<void>(`${this.constants.apiURL()}/subscription/addon/${createSubscriptionRequestDTO.influencer_id}/buy/transaction/`, createSubscriptionRequestDTO);
    }

    getDiscount(discountRequestDTO: DiscountRequestDTO): Observable<DiscountViewModel> {
        return this.httpClient.post<DiscountViewModel>(`${this.constants.apiURL()}/pricing/discount/`, discountRequestDTO);
    }

    isFreePlan(): Observable<boolean> {
        return new Observable((observer: Observer<boolean>) => {

            if (this.userSubscription === null) {
                forkJoin([
                    this.usersService.getMasterUserProfile(),
                    this.usersService.getUserSubscription()
                ]).subscribe(([masterProfile, subscription]) => {
                    this.userSubscription = subscription;
                    this.isMasterUser = masterProfile.is_master_profile === true;

                    if (this.isMasterUser) {
                        observer.next(false);
                        observer.complete();
                    }

                    if (this.userSubscription === null) {
                        observer.next(true);
                        observer.complete();
                    }

                    if (this.userSubscription.pricing_plan.id === 4) {
                        observer.next(true);
                        observer.complete();
                    }

                    if (this.userSubscription.pricing_plan.title === 'Безплатен' || this.userSubscription.pricing_plan.title === 'Free') {
                        observer.next(true);
                        observer.complete();
                    }

                    observer.next(false);
                    observer.complete();
                });
            } else {
                if (this.isMasterUser) {
                    observer.next(false);
                    observer.complete();
                }

                if (this.userSubscription === null) {
                    observer.next(true);
                    observer.complete();
                }

                if (this.userSubscription.pricing_plan.id === 4) {
                    observer.next(true);
                    observer.complete();
                }

                if (this.userSubscription.pricing_plan.title === 'Безплатен' || this.userSubscription.pricing_plan.title === 'Free') {
                    observer.next(true);
                    observer.complete();
                }

                observer.next(false);
                observer.complete();
            }
        });
    }

    isPermissionAllowed(permission: string): Observable<boolean> {
        return new Observable((observer: Observer<boolean>) => {

            // override and return that always allowed
            observer.next(true);
            observer.complete();

            // if (this.userSubscription === null) {
            //     this.usersService.getUserSubscription().subscribe(subscription => {
            //         this.userSubscription = subscription;
            //
            //         // free plan
            //         if (this.userSubscription === null || this.userSubscription.pricing_plan.id === 4 || this.userSubscription.pricing_plan.title === 'Безплатен' || this.userSubscription.pricing_plan.title === 'Free') {
            //             if (this.freePlanPermissions.includes(permission)) {
            //                 observer.next(true);
            //             } else {
            //                 observer.next(false);
            //             }
            //
            //             observer.complete();
            //         }
            //
            //         // light plan
            //         if (this.userSubscription !== null && this.userSubscription.pricing_plan.id === 9) {
            //             if (this.lightPlanPermissions.includes(permission)) {
            //                 observer.next(true);
            //             } else {
            //                 observer.next(false);
            //             }
            //
            //             observer.complete();
            //         }
            //
            //         // standart plan
            //         if (this.userSubscription !== null && this.userSubscription.pricing_plan.id === 7) {
            //             if (this.standartPlanPermissions.includes(permission)) {
            //                 observer.next(true);
            //             } else {
            //                 observer.next(false);
            //             }
            //
            //             observer.complete();
            //         }
            //
            //         observer.next(false);
            //         observer.complete();
            //     });
            //
            // } else {
            //     // free plan
            //     if (this.userSubscription === null || this.userSubscription.pricing_plan.id === 4 || this.userSubscription.pricing_plan.title === 'Безплатен' || this.userSubscription.pricing_plan.title === 'Free') {
            //         if (this.freePlanPermissions.includes(permission)) {
            //             observer.next(true);
            //         } else {
            //             observer.next(false);
            //         }
            //
            //         observer.complete();
            //     }
            //
            //     // light plan
            //     if (this.userSubscription !== null && this.userSubscription.pricing_plan.id === 9) {
            //         if (this.lightPlanPermissions.includes(permission)) {
            //             observer.next(true);
            //         } else {
            //             observer.next(false);
            //         }
            //
            //         observer.complete();
            //     }
            //
            //     // standart plan
            //     if (this.userSubscription !== null && this.userSubscription.pricing_plan.id === 7) {
            //         if (this.standartPlanPermissions.includes(permission)) {
            //             observer.next(true);
            //         } else {
            //             observer.next(false);
            //         }
            //
            //         observer.complete();
            //     }
            //
            //     observer.next(false);
            //     observer.complete();
            // }
        });
    }
}
